/**
 * Generates 2D grid coordinates for workflow steps.
 *
 * Steps that are part of a flow (at least one link in or out) are laid out as a
 * layered DAG: columns are critical-path depth (left to right by dependency),
 * rows are parallel steps at the same depth. Each weakly-connected flow gets its
 * own contiguous block of rows, stacked in input order, so unrelated flows never
 * interleave or leave gaps in each other's columns.
 *
 * Steps with no links ("isolated") aren't part of any flow, so they're packed
 * into a compact band across the top rows instead of stacking full-width, and
 * the connected flow starts in the rows below. The band is DEFAULT_ISOLATED_COLS
 * wide by default, but widens to match a longer connected flow so the standalone
 * steps reuse that same horizontal space instead of clumping to the left.
 * `compareIsolated` orders the band — the caller uses it to give webpage entry
 * points primacy.
 *
 * Example: A->B, B->C, A->C, C->D
 *
 *   col0  col1  col2
 * row0: A    B
 * row1:      C    D
 *
 * @returns Map of stepId to {row, col} coordinates
 */

const DEFAULT_ISOLATED_COLS = 4;

export function layoutWorkflowSteps<T extends { id: string }>(
  items: readonly T[],
  links: readonly { fromStepId: string; toStepId: string }[],
  options?: { compareIsolated?: (a: T, b: T) => number }
): Map<string, { row: number; col: number }> {
  const positions = new Map<string, { row: number; col: number }>();

  if (items.length === 0) {
    return positions;
  }

  // Build adjacency lists
  const incomingEdges = new Map<string, Set<string>>();
  const outgoingEdges = new Map<string, Set<string>>();

  for (const item of items) {
    incomingEdges.set(item.id, new Set());
    outgoingEdges.set(item.id, new Set());
  }

  for (const link of links) {
    const incoming = incomingEdges.get(link.toStepId);
    const outgoing = outgoingEdges.get(link.fromStepId);
    if (!incoming || !outgoing) continue;
    incoming.add(link.fromStepId);
    outgoing.add(link.toStepId);
  }

  // Steps with no links in or out aren't part of any flow; pack them into a
  // compact top band, then start the connected DAG in the rows below.
  const isolated: T[] = [];
  const connected: T[] = [];
  for (const item of items) {
    const degree =
      incomingEdges.get(item.id)!.size + outgoingEdges.get(item.id)!.size;
    (degree === 0 ? isolated : connected).push(item);
  }

  // Calculate column for each connected step using critical path (longest path
  // from any root). This ensures proper left-to-right ordering by dependency.
  const columns = new Map<string, number>();
  const visiting = new Set<string>(); // Track nodes being visited to detect cycles

  function calculateColumn(id: string): number {
    if (columns.has(id)) {
      return columns.get(id)!;
    }

    // Cycle detection: if we're already visiting this node, we have a cycle
    if (visiting.has(id)) {
      return 0;
    }

    const incoming = incomingEdges.get(id);
    if (!incoming || incoming.size === 0) {
      columns.set(id, 0);
      return 0;
    }

    visiting.add(id);

    let maxPredecessorCol = -1;
    for (const predId of incoming) {
      const predCol = calculateColumn(predId);
      maxPredecessorCol = Math.max(maxPredecessorCol, predCol);
    }

    visiting.delete(id);

    const col = maxPredecessorCol + 1;
    columns.set(id, col);
    return col;
  }

  for (const item of connected) {
    calculateColumn(item.id);
  }

  // Isolated steps default to a compact band, but widen to fill a longer flow so
  // they reuse the same horizontal space rather than clumping left.
  const dagColCount = columns.size > 0 ? Math.max(...columns.values()) + 1 : 0;
  const bandWidth = Math.min(
    isolated.length,
    Math.max(DEFAULT_ISOLATED_COLS, dagColCount)
  );
  const bandRowCount =
    bandWidth > 0 ? Math.ceil(isolated.length / bandWidth) : 0;

  const orderedIsolated = options?.compareIsolated
    ? [...isolated].sort(options.compareIsolated)
    : isolated;

  orderedIsolated.forEach((item, i) => {
    positions.set(item.id, {
      row: Math.floor(i / bandWidth),
      col: i % bandWidth,
    });
  });

  // Partition connected steps into weakly-connected components (individual
  // flows), so each flow can own a contiguous block of rows rather than sharing
  // the row space with unrelated flows and interleaving.
  const componentId = new Map<string, number>();
  let componentCount = 0;
  for (const item of connected) {
    if (componentId.has(item.id)) continue;
    const id = componentCount++;
    componentId.set(item.id, id);
    const queue = [item.id];
    while (queue.length > 0) {
      const current = queue.pop()!;
      for (const neighbor of [
        ...incomingEdges.get(current)!,
        ...outgoingEdges.get(current)!,
      ]) {
        if (!componentId.has(neighbor)) {
          componentId.set(neighbor, id);
          queue.push(neighbor);
        }
      }
    }
  }

  // Order the flows by their earliest-listed member so the stack is stable and
  // follows the order steps appear in the workflow.
  const inputIndex = new Map(items.map((item, i) => [item.id, i]));
  const componentMembers = new Map<number, string[]>();
  for (const item of connected) {
    const id = componentId.get(item.id)!;
    const members = componentMembers.get(id) ?? [];
    members.push(item.id);
    componentMembers.set(id, members);
  }
  const orderedComponents = [...componentMembers.values()].sort(
    (a, b) =>
      Math.min(...a.map((id) => inputIndex.get(id)!)) -
      Math.min(...b.map((id) => inputIndex.get(id)!))
  );

  // Assign rows one flow at a time, each starting at local row 0, then stacked
  // below the previous flow (row cursor) so blocks never interleave. Columns
  // stay global critical-path depth, so every flow's roots sit at col 0.
  const dagPositions = new Map<string, { row: number; col: number }>();
  let rowCursor = 0;

  for (const memberIds of orderedComponents) {
    const columnGroups = new Map<number, string[]>();
    for (const id of memberIds) {
      const col = columns.get(id)!;
      const group = columnGroups.get(col) ?? [];
      group.push(id);
      columnGroups.set(col, group);
    }

    // DAG-local rows for this flow (row 0 = its first row).
    const local = new Map<string, { row: number; col: number }>();
    const maxCol = Math.max(...columnGroups.keys());
    for (let col = 0; col <= maxCol; col++) {
      for (const itemId of columnGroups.get(col) ?? []) {
        // Predecessors are always in an earlier column of the same flow, so
        // they're already placed. Reuse a lone predecessor's row (sequential
        // chain); after a merge, drop to a fresh row below the deepest input.
        const predRows = Array.from(incomingEdges.get(itemId)!)
          .map((predId) => local.get(predId)?.row)
          .filter((r): r is number => r !== undefined);

        let targetRow = 0;
        if (predRows.length === 1) {
          targetRow = predRows[0]!;
        } else if (predRows.length > 1) {
          targetRow = Math.max(...predRows) + 1;
        }

        const usedRows = new Set(
          Array.from(local.values())
            .filter((p) => p.col === col)
            .map((p) => p.row)
        );
        let row = targetRow;
        while (usedRows.has(row)) row++;

        local.set(itemId, { row, col });
      }
    }

    const height =
      local.size > 0
        ? Math.max(...Array.from(local.values(), (p) => p.row)) + 1
        : 0;
    for (const [id, pos] of local) {
      dagPositions.set(id, { row: pos.row + rowCursor, col: pos.col });
    }
    rowCursor += height;
  }

  for (const [id, pos] of dagPositions) {
    positions.set(id, { row: pos.row + bandRowCount, col: pos.col });
  }

  return positions;
}
